import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReferenceResponseService, type Email } from '../../../../../js/api';
import { Button } from '../../../components/button';
import { Field, Fieldset, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { Section } from '../../../components/section';
import { SectionHeader } from '../../../components/SectionHeader';
import { Text } from '../../../components/text';

// TODO: derive this from the correct model
interface ReferenceData {
  id: string;
  current_status: {
    code: string;
    title: string;
    category: string;
    category_title: string;
    category_kind: string;
  };
  reference: {
    provider: {
      prefix_name: string;
      preferred_name: string;
      last_name: string;
    };
  };
}

interface ProviderReferenceEmailLookupProps {
  onFoundEndorsementId?: (endorsementId: string) => void;
}

export const ProviderReferenceEmailLookup: React.FC<ProviderReferenceEmailLookupProps> = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [references, setReferences] = useState<ReferenceData[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setReferences([]);
    setIsSubmitting(true);
    try {
      const payload: Email = { email };
      console.log('Looking up outstanding endorsements for email:', email);

      const result = await ReferenceResponseService.providerReferenceResponsesOutstanding({
        requestBody: payload,
      });

      const referencesData = Array.isArray(result) ? result : result?.results || [];

      if (referencesData.length > 0) {
        setReferences(referencesData as ReferenceData[]);
        setMessage(`Found ${referencesData.length} outstanding endorsement request(s).`);
      } else {
        setMessage('No outstanding endorsement requests found for this email.');
      }
    } catch (err) {
      console.error('Error fetching endorsements:', err);
      setError('There was an issue looking up your endorsements. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBeginEndorsement = (endorsementId: string) => {
    navigate(`/endorsement/provider-reference/${endorsementId}`);
  };

  return (
    // TODO DARK MODE STYLES: this needs to be a global component
    <main className="tw:max-w-2xl tw:mx-auto tw:px-4 tw:pt-6 tw:pb-10 tw:dark:bg-zinc-700">
      <Section>
        <SectionHeader headerText="Find Your Reference Request" />
        <Text>
          Enter the email address where you received the reference request. We&apos;ll look up
          outstanding requests so you can complete the reference.
        </Text>
        <form onSubmit={handleSubmit}>
          <Fieldset>
            <Field>
              <Label>Email address</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
          </Fieldset>
          {error && <Text className="tw:!text-stoplight-danger tw:!mt-3">{error}</Text>}
          {message && <Text className="tw:!mt-3">{message}</Text>}
          <div className="tw:flex tw:justify-end tw:mt-6">
            <Button color="brandgreen0" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Searching…' : 'Find reference(s)'}
            </Button>
          </div>
        </form>

        {references.length > 0 && (
          <div className="tw:mt-6">
            <SectionHeader
              headerText="Outstanding Reference Requests"
              className="tw:!font-medium tw:mb-4"
            />
            <div className="tw:space-y-4">
              {references.map((reference) => (
                <div
                  key={reference.id}
                  // TODO DARK MODE STYLES: these need to be defined as global section-with-section styles
                  className="tw:border tw:border-zinc-200 tw:rounded-lg tw:p-4 tw:bg-zinc-50 tw:dark:bg-white/5 tw:dark:border-white/10"
                >
                  <div className="tw:flex tw:justify-between tw:items-center">
                    <div className="tw:flex-1">
                      <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
                        {/* <span className="tw:font-medium">Reference ID: {reference.id}</span> */}
                        <span className="tw:font-medium tw:dark:text-white">
                          {reference.reference.provider.prefix_name}{' '}
                          {reference.reference.provider.preferred_name}{' '}
                          {reference.reference.provider.last_name}
                        </span>
                        {/* TODO: do we need a component for this status pill? only outstanding statuses will show here */}
                        <span className="tw:px-2 tw:py-1 tw:bg-blue-100 tw:text-blue-800 tw:text-xs tw:rounded-full">
                          {reference.current_status.title}
                        </span>
                      </div>
                    </div>
                    <div className="tw:ml-4">
                      <Button
                        color="brandgreen0"
                        onClick={() => handleBeginEndorsement(reference.id)}
                      >
                        {reference.current_status.category_kind === 'I' ? 'Begin' : 'Resume'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>
    </main>
  );
};

export default ProviderReferenceEmailLookup;
